import { ShipmentHeaderService } from '../../modules/shared/services/shipment-header.service';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
    shipmentTableAddAssist,
    shipmentTableChangePage, shipmentTableChangeQuery, shipmentTableCreateView, shipmentTableDeleteView,
    shipmentTableFailure, shipmentTableRemoveAssist,
    shipmentTableRequest, shipmentTableSort,
    shipmentTableSuccess, shipmentTableUpdateComplete, shipmentTableUpdateFailure, shipmentTableUpdatePreSelectedQuote
} from './shipments-table.actions';
import { catchError, concatMap, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { AppState } from '../index';
import { selectShipmentsTableAsView, selectShipmentsTableQuery } from './shipments-table.state';
import { ShipmentFilterService } from '../../modules/shared/services/shipment-filter.service';
import { selectSetting } from '../profile/profile.state';
import { SettingEnum } from '../../enum/setting.enum';
import { profileSubmitSetting } from '../profile/profile.actions';
import { GUID } from '../../utils/guid';
import { ShipmentTableExportService } from '../../modules/shared/services/shipment-table-export.service';
import { interfaceExport, interfaceExportComplete, interfaceExportError, interfaceExportStart } from '../interface/interface.actions';
import { selectExportProvider } from '../interface/interface.state';
import { ExportProviderEnum } from '../../enum/export-provider.enum';
import { ShipmentService } from '../../data/shipment.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable()
export class ShipmentsTableEffects {

    constructor(private actions$: Actions, private shipmentHeaderService: ShipmentHeaderService, private store: Store<AppState>,
                private snackbar: MatSnackBar, private shipmentFilterService: ShipmentFilterService,
                private shipmentTableExportService: ShipmentTableExportService, private shipmentService: ShipmentService) {
    }

    shipmentTableRequest$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentTableRequest),
        concatMap(action => of(action).pipe(
            withLatestFrom(this.store.pipe(select(selectShipmentsTableQuery)))
        )),
        mergeMap(([ , query ]) => this.shipmentHeaderService.query(query).pipe(
            map((result) => shipmentTableSuccess(result))
        )),
        catchError((e) => of(shipmentTableFailure()))
    ));

    shipmentTableFailure$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentTableFailure),
        tap(() => this.snackbar.open($localize`We are having difficulty communicating with the server.  Please try again later.`))
    ), { dispatch: false });

    shipmentTableChangePage$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentTableChangePage),
        map(() => shipmentTableRequest())
    ));

    shipmentTableChangeSort$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentTableSort),
        map(() => shipmentTableRequest())
    ));

    shipmentFilterAdd$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentTableAddAssist),
        withLatestFrom(this.store.select(selectShipmentsTableQuery)),
        map(([ action, query ]) => this.shipmentFilterService.applyAssist(query, action.id, action.parameters)),
        map(query => shipmentTableChangeQuery({ query }))
    ));

    shipmentFilterRemove$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentTableRemoveAssist),
        withLatestFrom(this.store.select(selectShipmentsTableQuery)),
        map(([ action, query ]) => this.shipmentFilterService.removeAssist(query, action.id)),
        map(query => shipmentTableChangeQuery({ query }))
    ));

    shipmentTableChangeQuery$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentTableChangeQuery),
        map(() => shipmentTableRequest())
    ));

    shipmentTableCreateView$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentTableCreateView),
        withLatestFrom(
            this.store.select(selectSetting(SettingEnum.USER_SHIPMENT_VIEWS)),
            this.store.select(selectShipmentsTableAsView)
        ),
        map(([ action, setting, view ]) => profileSubmitSetting({
            id: SettingEnum.USER_SHIPMENT_VIEWS,
            value: [ ...(setting ? setting : []), { ...view, id: GUID.create(), caption: action.name } ]
        }))
    ));

    shipmentTableDeleteView$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentTableDeleteView),
        withLatestFrom(this.store.select(selectSetting(SettingEnum.USER_SHIPMENT_VIEWS))),
        map(([ action, views ]) => profileSubmitSetting({
            id: SettingEnum.USER_SHIPMENT_VIEWS,
            value: [ ...views.filter(v => v.id !== action.id) ]
        }))
    ));

    shipmentTableExport$ = createEffect(() => this.actions$.pipe(
        ofType(interfaceExport),
        withLatestFrom(this.store.select(selectExportProvider)),
        filter(([ , provider ]) => provider === ExportProviderEnum.SHIPMENT_TABLE),
        tap(() => this.store.dispatch(interfaceExportStart())),
        switchMap(([ action ]) => this.shipmentTableExportService.export(action.format)),
        map(() => interfaceExportComplete()),
        catchError(() => of(interfaceExportError()))
    ));

    shipmentTableUpdatePreSelectedQuote = createEffect(() => this.actions$.pipe(
        ofType(shipmentTableUpdatePreSelectedQuote),
        switchMap((action) => this.shipmentService.changePreSelectedQuote(action.shipmentId, action.quoteId).pipe(
            map((result) => shipmentTableUpdateComplete({ shipmentId: result.id })),
            catchError(() => of(shipmentTableUpdateFailure({ shipmentId: action.shipmentId })))
        ))
    ));

}
